/**
 * Centralized API client for the DPDP dashboard.
 *
 * Wraps fetch() with:
 * - Auth token injection (session JWT)
 * - JSON parsing with error normalization
 * - Optional API-key mode for legacy consent-engine calls
 */

import { STORAGE_KEYS } from './constants';

const BASE_URL =
  import.meta.env.VITE_API_BASE_URL || 'https://dpdp-consent-api-staging.shambhurajqx.workers.dev';

// ---------------------------------------------------------------------------
// Token storage helpers
// ---------------------------------------------------------------------------

export function getSessionToken(): string | null {
  return localStorage.getItem(STORAGE_KEYS.TOKEN);
}

export function setSessionToken(token: string): void {
  localStorage.setItem(STORAGE_KEYS.TOKEN, token);
}

export function clearSessionToken(): void {
  localStorage.removeItem(STORAGE_KEYS.TOKEN);
}

export function getApiKey(): string | null {
  return localStorage.getItem(STORAGE_KEYS.API_KEY);
}

export function setApiKey(key: string, appId: string): void {
  localStorage.setItem(STORAGE_KEYS.API_KEY, key);
  localStorage.setItem(STORAGE_KEYS.APP_ID, appId);
}

export function clearApiKey(): void {
  localStorage.removeItem(STORAGE_KEYS.API_KEY);
  localStorage.removeItem(STORAGE_KEYS.APP_ID);
}

export function getActiveOrgId(): string | null {
  return localStorage.getItem(STORAGE_KEYS.ACTIVE_ORG);
}

export function setActiveOrgId(orgId: string): void {
  localStorage.setItem(STORAGE_KEYS.ACTIVE_ORG, orgId);
}

export function clearActiveOrgId(): void {
  localStorage.removeItem(STORAGE_KEYS.ACTIVE_ORG);
}

// ---------------------------------------------------------------------------
// Error type
// ---------------------------------------------------------------------------

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// ---------------------------------------------------------------------------
// Core fetch wrapper
// ---------------------------------------------------------------------------

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** Use API key auth instead of session token (for consent-engine routes) */
  useApiKey?: boolean;
  /** Expect a blob response (for PDF downloads) */
  blob?: boolean;
  /** Custom headers */
  headers?: Record<string, string>;
}

export async function apiFetch<T = unknown>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const { method = 'GET', body, useApiKey = false, blob = false, headers = {} } = options;

  const finalHeaders: Record<string, string> = { ...headers };

  // Inject auth
  if (useApiKey) {
    const apiKey = getApiKey();
    if (apiKey) {
      finalHeaders.Authorization = `Bearer ${apiKey}`;
    }
  } else {
    const token = getSessionToken();
    if (token) {
      finalHeaders.Authorization = `Bearer ${token}`;
    }
    const activeOrgId = getActiveOrgId();
    if (activeOrgId) {
      finalHeaders['X-Organisation-Id'] = activeOrgId;
    }
  }

  if (body !== undefined && !blob) {
    finalHeaders['Content-Type'] = 'application/json';
  }

  const response = await fetch(`${BASE_URL}${path}`, {
    method,
    headers: finalHeaders,
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });

  if (!response.ok) {
    let code = 'INTERNAL_ERROR';
    let message = 'Something went wrong';
    try {
      const errorBody = await response.json();
      code = errorBody?.error?.code || code;
      message = errorBody?.error?.message || message;
    } catch {
      // Non-JSON error, use defaults
    }
    throw new ApiError(response.status, code, message);
  }

  if (blob) {
    return (await response.blob()) as unknown as T;
  }

  return (await response.json()) as T;
}

// ---------------------------------------------------------------------------
// Network error detection (preserved from original dashboard)
// ---------------------------------------------------------------------------

export function getNetworkErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof Error && err.name === 'TypeError' && err.message.includes('fetch')) {
    return 'API server not running! (Requires Node.js v20 LTS for local development.)';
  }
  if (err instanceof ApiError) {
    return err.message;
  }
  if (err instanceof Error) {
    return err.message || fallback;
  }
  return fallback;
}

// ---------------------------------------------------------------------------
// Team API functions
// ---------------------------------------------------------------------------

import type { NoticeComparison, RollbackResponse } from '@dpdp/sdk';
import type {
  CreateInvitationResponse,
  GetInvitationResponse,
  JoinOrganisationResponse,
  ListMembersResponse,
  NoticeVersionSummary,
  MultilingualNotice,
} from './types';

export async function listMembers(orgId: string): Promise<ListMembersResponse> {
  return apiFetch<ListMembersResponse>(`/v1/organisations/${orgId}/members`);
}

export async function createInvitation(
  orgId: string,
  email: string,
  role: 'owner' | 'member',
): Promise<CreateInvitationResponse> {
  return apiFetch<CreateInvitationResponse>(`/v1/organisations/${orgId}/invitations`, {
    method: 'POST',
    body: { email, role },
  });
}

export async function removeMember(orgId: string, memberId: string): Promise<{ success: true }> {
  return apiFetch<{ success: true }>(`/v1/organisations/${orgId}/members/${memberId}/remove`, {
    method: 'POST',
  });
}

export async function getInvitation(token: string): Promise<GetInvitationResponse> {
  return apiFetch<GetInvitationResponse>(`/v1/invitations/${token}`);
}

export async function joinOrganisation(inviteToken: string): Promise<JoinOrganisationResponse> {
  return apiFetch<JoinOrganisationResponse>('/v1/organisations/join', {
    method: 'POST',
    body: { inviteToken },
  });
}

// ---------------------------------------------------------------------------
// Notice API functions (Enterprise Consent Notice Designer)
// ---------------------------------------------------------------------------

export async function listNoticeVersions(applicationId: string): Promise<{ notices: NoticeVersionSummary[] }> {
  return apiFetch<{ notices: NoticeVersionSummary[] }>(
    `/v1/applications/${applicationId}/notices`,
    { useApiKey: true },
  );
}

/**
 * Fetch a single notice version by ID (Authenticated).
 * Targets: GET /v1/notices/:noticeId — the authenticated single-version endpoint.
 * This is distinct from the public GET /v1/notices/active/:applicationId endpoint.
 */
export async function getNoticeDetail(noticeId: string): Promise<MultilingualNotice> {
  return apiFetch<MultilingualNotice>(`/v1/notices/${noticeId}`, { useApiKey: true });
}

/**
 * Compare two notice versions via POST /v1/notices/:noticeId/compare.
 * Matches the SDK's compareNotices(noticeId, otherNoticeId) contract.
 * Reuses NoticeComparison type from @dpdp/sdk — no duplicate types.
 */
export async function compareNoticeVersions(
  noticeId: string,
  otherNoticeId: string,
): Promise<NoticeComparison> {
  return apiFetch<NoticeComparison>(`/v1/notices/${noticeId}/compare`, {
    method: 'POST',
    body: { otherNoticeId },
    useApiKey: true,
  });
}

/**
 * Rollback to a previous notice version via POST /v1/notices/:noticeId/rollback.
 * Matches the SDK's rollbackNotice(noticeId) contract.
 * Reuses RollbackResponse type from @dpdp/sdk — no duplicate types.
 *
 * Note: rollback does NOT overwrite history. The backend creates a NEW version
 * using the selected notice as its source, and archives the previously live version.
 */
export async function rollbackNoticeVersion(noticeId: string): Promise<RollbackResponse> {
  return apiFetch<RollbackResponse>(`/v1/notices/${noticeId}/rollback`, {
    method: 'POST',
    useApiKey: true,
  });
}

// ---------------------------------------------------------------------------
// Enterprise Approval Workflow API functions
// (Session-based — these are user-scoped org actions, not API-key calls.
//  apiFetch injects the session JWT + X-Organisation-Id header automatically.)
// ---------------------------------------------------------------------------

import type {
  AddCommentResponse,
  CreateDraftResponse,
  NoticeDraftDetail,
  NoticeDraftSummary,
  PublishDraftResponse,
  WorkflowActionResponse,
} from '@dpdp/sdk';

export async function createDraft(content: MultilingualNotice): Promise<CreateDraftResponse> {
  return apiFetch<CreateDraftResponse>('/v1/notices/drafts', {
    method: 'POST',
    body: { content },
  });
}

export async function listDrafts(applicationId: string): Promise<{ drafts: NoticeDraftSummary[] }> {
  return apiFetch<{ drafts: NoticeDraftSummary[] }>(
    `/v1/applications/${applicationId}/notices/drafts`,
  );
}

export async function getDraft(draftId: string): Promise<NoticeDraftDetail> {
  return apiFetch<NoticeDraftDetail>(`/v1/notices/drafts/${draftId}`);
}

export async function updateDraft(
  draftId: string,
  content: MultilingualNotice,
): Promise<WorkflowActionResponse> {
  return apiFetch<WorkflowActionResponse>(`/v1/notices/drafts/${draftId}`, {
    method: 'PUT',
    body: { content },
  });
}

export async function submitDraft(draftId: string, comment?: string): Promise<WorkflowActionResponse> {
  return apiFetch<WorkflowActionResponse>(`/v1/notices/drafts/${draftId}/submit`, {
    method: 'POST',
    body: comment ? { comment } : {},
  });
}

export async function approveDraft(draftId: string, comment?: string): Promise<WorkflowActionResponse> {
  return apiFetch<WorkflowActionResponse>(`/v1/notices/drafts/${draftId}/approve`, {
    method: 'POST',
    body: comment ? { comment } : {},
  });
}

export async function rejectDraft(draftId: string, comment: string): Promise<WorkflowActionResponse> {
  return apiFetch<WorkflowActionResponse>(`/v1/notices/drafts/${draftId}/reject`, {
    method: 'POST',
    body: { comment },
  });
}

export async function returnDraft(draftId: string, comment: string): Promise<WorkflowActionResponse> {
  return apiFetch<WorkflowActionResponse>(`/v1/notices/drafts/${draftId}/return`, {
    method: 'POST',
    body: { comment },
  });
}

export async function reviseDraft(draftId: string): Promise<WorkflowActionResponse> {
  return apiFetch<WorkflowActionResponse>(`/v1/notices/drafts/${draftId}/revise`, {
    method: 'POST',
  });
}

export async function publishDraft(draftId: string): Promise<PublishDraftResponse> {
  return apiFetch<PublishDraftResponse>(`/v1/notices/drafts/${draftId}/publish`, {
    method: 'POST',
  });
}

export async function addReviewComment(
  draftId: string,
  comment: string,
): Promise<AddCommentResponse> {
  return apiFetch<AddCommentResponse>(`/v1/notices/drafts/${draftId}/comments`, {
    method: 'POST',
    body: { comment },
  });
}
